/**
 * ReservationSheet - Drawer lateral per mostrar detalls d'una reserva
 *
 * Reutilitzable a: Calendar V2, Llistat, Miranda Ops, Dashboard
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMiranda } from '@/contexts/MirandaContext';
import { cn } from '@/lib/utils';
import { pickGuestFormUrl } from '@/lib/messaging';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getCountryName, countryCodeToNationality } from '@/lib/data/countries';

// Helper para detectar idioma desde código de país (alpha-2)
function detectLanguageFromCountry(countryCode: string): string {
  const map: Record<string, string> = {
    // Español
    ES: 'es',
    MX: 'es',
    AR: 'es',
    CO: 'es',
    PE: 'es',
    CL: 'es',
    DO: 'es',
    VE: 'es',
    EC: 'es',
    BO: 'es',
    PY: 'es',
    UY: 'es',
    CU: 'es',
    GT: 'es',
    HN: 'es',
    SV: 'es',
    NI: 'es',
    CR: 'es',
    PA: 'es',
    PR: 'es', // Puerto Rico
    // Inglés
    GB: 'en',
    US: 'en',
    CA: 'en',
    IE: 'en',
    AU: 'en',
    NZ: 'en',
    ZA: 'en',
    // Francés
    FR: 'fr',
    BE: 'fr',
    CH: 'fr',
    // Catalán
    AD: 'ca',
    // Alemán
    DE: 'de',
    AT: 'de',
    // Italiano
    IT: 'it',
    // Portugués
    PT: 'pt',
    BR: 'pt',
    // Otros
    NL: 'nl',
    SE: 'sv',
    NO: 'no',
    DK: 'da',
    FI: 'fi',
    PL: 'pl',
  };
  return map[countryCode.toUpperCase()] || 'en';
}
import {
  Calendar,
  Users,
  CreditCard,
  Building2,
  Phone,
  Mail,
  Key,
  X,
  Receipt,
  Trash2,
  Loader2,
  MessageSquare,
  Copy,
  Check,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ReservationCellData } from '@/components/calendar-v2';
import { ClientSearch } from '@/components/clients/ClientSearch';
import { api } from '@/services/api';
import { reservationSaveToast, showResultToast } from '@/lib/beds24-simulated-toast';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { EditViatgerModal } from '@/components/viatgers/EditViatgerModal';
import { AddViatgerDialog } from './AddViatgerDialog';
import { EditStayButton } from './EditStayDialog';
import { AddStayToGroupButton } from './AddStayToGroupDialog';
import { UserPlus } from 'lucide-react';
import { DebugPanel } from '@/components/dev/DebugPanel';
import { MessagingBadge } from '@/components/messaging/MessagingBadge';
import { QuickWhatsAppActions } from '@/components/messaging/QuickWhatsAppActions';
import { ReservationMessagesTab } from '@/components/messaging/ReservationMessagesTab';
import { ReservationUseCasesSection } from '@/components/use-cases/ReservationUseCasesSection';
import { useGenerateGroupForm } from '@/hooks/useGenerateGroupForm';

// ============================================
// TYPES
// ============================================

export interface ReservationSheetProps {
  reservation: ReservationCellData | null;
  open: boolean;
  onClose: () => void;
}

// ============================================
// HELPERS
// ============================================

function getStatusBadge(status?: string) {
  const config: Record<
    string,
    { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
  > = {
    confirmed: { label: 'Confirmada', variant: 'default' },
    pending: { label: 'Pendent', variant: 'secondary' },
    checked_in: { label: 'Check-in', variant: 'default' },
    checked_out: { label: 'Check-out', variant: 'outline' },
    cancelled: { label: 'Cancel·lada', variant: 'destructive' },
  };
  const { label, variant } = config[status || 'confirmed'] || config.confirmed;
  return <Badge variant={variant}>{label}</Badge>;
}

function getChannelColor(channel: string): string {
  const colors: Record<string, string> = {
    'Booking.com': 'bg-blue-500',
    Airbnb: 'bg-rose-500',
    Expedia: 'bg-yellow-500',
    Direct: 'bg-green-500',
    Directe: 'bg-green-500',
  };
  return colors[channel] || 'bg-gray-500';
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ca-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// ============================================
// COMPONENT
// ============================================

export function ReservationSheet({ reservation, open, onClose }: ReservationSheetProps) {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  // 🆕 F2 — Miranda al costat. La fitxa i el panell s'han de veure a la vegada: quan Miranda
  // és oberta, a `≥ lg` la fitxa es desplaça 380 px cap a l'esquerra i el panell ocupa la
  // columna dreta. Per sota de `lg` no hi caben dues columnes i es comporta com abans.
  const { isOpen: mirandaOpen, open: openMiranda } = useMiranda();

  // Client seleccionat manualment (override del guardat)
  const [manuallySelectedClient, setManuallySelectedClient] = useState<{
    client: any;
    forReservationId: string;
  } | null>(null);
  const [currentLang, setCurrentLang] = useState<string>(reservation?.lang || '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState<string>(reservation?.guestPhone || '');
  const [editingCountry, setEditingCountry] = useState(false);
  const [countryValue, setCountryValue] = useState<string>(reservation?.guestCountry2 || '');
  const [copiedFormUrl, setCopiedFormUrl] = useState(false);

  useEffect(() => {
    setCurrentLang(reservation?.lang || '');
    setPhoneValue(reservation?.guestPhone || '');
    setCountryValue(reservation?.guestCountry2 || '');
    setEditingPhone(false);
    setEditingCountry(false);
  }, [reservation?.id]);

  // Estats per llistat viatgers expandible
  const [showGuestsList, setShowGuestsList] = useState(false);
  const [editingGuest, setEditingGuest] = useState<any | null>(null);
  const [showEditGuestModal, setShowEditGuestModal] = useState(false);
  const [showAddGuestDialog, setShowAddGuestDialog] = useState(false);

  // Carregar metadata de la reserva (inclou clientId + accessTokens)
  const { data: metadata } = useQuery({
    queryKey: ['reservation-metadata', reservation?.id],
    queryFn: async () => {
      const res = await api.get(`/reservations/${reservation!.id}`);
      return res.data?.metadata || null;
    },
    enabled: !!reservation?.id && open,
  });

  // Obtenir el link del formulari de registre (primer accessToken actiu)
  const { data: reservationFull } = useQuery({
    queryKey: ['reservation-full', reservation?.id],
    queryFn: async () => {
      const res = await api.get(`/reservations/${reservation!.id}`);
      return res.data;
    },
    enabled: !!reservation?.id && open,
  });

  /**
   * 🆕 F3.5 — Germanes del grup de Beds24, per si el cas s'ha de lligar a tot el grup.
   *
   * Mateixa clau que el focus de `CalendarV2Page` (`['reservation-by-beds24', id]`): quan
   * la fitxa s'obre des de l'URL, la resposta ja és a la cau i no hi ha cap crida de més.
   * `group_beds24_ids` només el dona aquest endpoint, no `GET /reservations/:id`.
   */
  const { data: reservationByBeds24 } = useQuery({
    queryKey: ['reservation-by-beds24', reservation?.beds24Id],
    queryFn: async () => {
      const res = await api.get(`/reservations/by-beds24/${reservation!.beds24Id}`);
      return res.data;
    },
    enabled: !!reservation?.beds24Id && open,
    retry: false,
  });

  const groupBeds24Ids: string[] = reservationByBeds24?.group_beds24_ids ?? [];
  const generateGroupForm = useGenerateGroupForm();

  // El formulari que encara obre (ni caducat ni revocat): el Registre en pot haver creat un de nou.
  const formUrl = pickGuestFormUrl(reservationFull?.accessTokens);

  const handleCopyFormUrl = async () => {
    if (!formUrl) return;
    try {
      await navigator.clipboard.writeText(formUrl);
      setCopiedFormUrl(true);
      setTimeout(() => setCopiedFormUrl(false), 2000);
    } catch {
      toast.error('Error copiant el link');
    }
  };

  // Carregar client guardat si hi ha clientId a metadata
  const { data: savedClient } = useQuery({
    queryKey: ['client', metadata?.clientId],
    queryFn: async () => {
      const res = await api.get(`/clients/${metadata!.clientId}`);
      return res.data;
    },
    enabled: !!metadata?.clientId,
  });

  // Carregar viatgers de la reserva
  const {
    data: guests = [],
    isLoading: loadingGuests,
    error: guestsError,
  } = useQuery({
    queryKey: ['reservation-guests', reservation?.id],
    queryFn: async () => {
      if (!reservation?.id) return [];
      const res = await api.get(`/guest-portal/reservations/${reservation.id}/guests`);

      // Adaptar dades del backend al format Viatger
      return res.data.map((guest: any) => ({
        id: guest.id,
        reserva_id: guest.reservationId,
        nom: guest.person?.firstName || guest.firstName || '',
        cognoms: guest.person?.lastName || guest.lastName || '',
        email: guest.person?.email || guest.email || '',
        telefon: guest.person?.phone || guest.phone || '',
        mobil: guest.person?.mobile || guest.mobile || '',
        tipus_document: guest.person?.documentType || guest.documentType || 'D',
        num_document: guest.person?.documentNumber || guest.documentNumber || '',
        nacionalitat: guest.person?.nationality || guest.nationality || 'ES',
        data_naixement: guest.person?.dateOfBirth || guest.dateOfBirth || '',
        sexe: guest.person?.sex || guest.sex || '',
        adreca: guest.person?.address || guest.address || '',
        ciutat: guest.person?.city || guest.city || '',
        provincia: guest.person?.province || '',
        codi_postal: guest.person?.postalCode || guest.postalCode || '',
        pais: guest.person?.country || guest.country || 'ES',
        peticions_especials: guest.specialRequests || '',
        es_titular: guest.isPrimary || false,
        consentiment_rgpd: guest.gdprConsent || false,
        consentiment_marketing: false,
        created_at: guest.createdAt,
        updated_at: guest.updatedAt,
      }));
    },
    enabled: !!reservation?.id && open,
  });

  // Client a mostrar: manual (si és per aquesta reserva) > saved
  const selectedClient =
    (manuallySelectedClient && manuallySelectedClient.forReservationId === reservation?.id
      ? manuallySelectedClient.client
      : null) ||
    savedClient ||
    null;

  // Mutació per guardar el client a la reserva
  const saveClientMutation = useMutation({
    mutationFn: async ({
      reservationId,
      clientId,
    }: {
      reservationId: string;
      clientId: string;
    }) => {
      console.log('🔄 Saving client to reservation:', { reservationId, clientId });
      const res = await api.patch(`/reservations/${reservationId}/metadata`, { clientId });
      console.log('✅ Response:', res.data);
      return res.data;
    },
    onSuccess: (data) => {
      console.log('✅ Client saved successfully:', data);
      // Fora de producció el `custom1` de Beds24 no s'escriu (`beds24Simulated`): avís propi.
      showResultToast(reservationSaveToast(data, 'Client assignat correctament'));
      // Invalidar cache del calendari per actualitzar el badge instantàniament
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
    },
    onError: (error: Error) => {
      console.error('❌ Error saving client:', error);
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleClientSelect = (client: any) => {
    if (client && reservation) {
      setManuallySelectedClient({ client, forReservationId: reservation.id });
      saveClientMutation.mutate({ reservationId: reservation.id, clientId: client.id });
    }
  };

  // Mutació per guardar telèfon
  const savePhoneMutation = useMutation({
    mutationFn: async ({ reservationId, phone }: { reservationId: string; phone: string }) => {
      const res = await api.patch(`/reservations/${reservationId}`, { guestPhone: phone || null });
      return res.data;
    },
    onSuccess: (data) => {
      // Fora de producció el telèfon es desa en local i no surt cap a Beds24 (`beds24Simulated`).
      showResultToast(reservationSaveToast(data, 'Telèfon actualitzat'));
      setEditingPhone(false);
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Mutació per guardar país
  const saveCountryMutation = useMutation({
    mutationFn: async ({ reservationId, country }: { reservationId: string; country: string }) => {
      const res = await api.patch(`/reservations/${reservationId}`, {
        guestCountry2: country || null,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('País actualitzat');
      setEditingCountry(false);
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Mutació per guardar idioma de comunicació
  const saveLangMutation = useMutation({
    mutationFn: async ({ reservationId, lang }: { reservationId: string; lang: string }) => {
      const res = await api.patch(`/reservations/${reservationId}`, { lang: lang || null });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Idioma de comunicació actualitzat');
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleLangChange = (lang: string) => {
    setCurrentLang(lang);
    if (reservation) {
      saveLangMutation.mutate({ reservationId: reservation.id, lang });
    }
  };

  // Mutació per esborrar el client
  const clearClientMutation = useMutation({
    mutationFn: async (reservationId: string) => {
      const res = await api.patch(`/reservations/${reservationId}/metadata`, { clientId: null });
      return res.data;
    },
    onSuccess: (data) => {
      showResultToast(reservationSaveToast(data, 'Client eliminat'));
      setManuallySelectedClient(null);
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['reservation-metadata', reservation?.id] });
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleClearClient = () => {
    if (reservation) {
      clearClientMutation.mutate(reservation.id);
    }
  };

  if (!reservation) return null;

  const {
    beds24Id,
    guestName,
    channel,
    checkIn,
    checkOut,
    guestsRegistered,
    guestsExpected,
    companyName,
    status,
    keyStatus,
    paymentStatus,
  } = reservation;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        // La fitxa ja té la seva X a la capçalera: sense això se'n veien dues (09-SET-2026).
        hideClose
        className={cn(
          'w-full sm:max-w-md overflow-y-auto overflow-x-hidden shadow-2xl mt-[120px] h-[calc(100%-120px)]',
          // Amb Miranda oberta, la fitxa s'aparta ja a `md`: abans només ho feia a `lg` i
          // entre 768 i 1023 px els dos panells s'ancoraven tots dos a la dreta i es
          // tapaven (§11 de `plans/miranda-grups-i-torns.md`).
          mirandaOpen && 'md:right-[340px] md:max-w-sm lg:right-[380px] lg:max-w-md'
        )}
        onInteractOutside={(e) => {
          // Permet interacció amb popovers (ClientSearch, etc.)
          const target = e.target as HTMLElement;
          if (target.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
            return;
          }
          // Controls de fora de la fitxa que no l'han de tancar (barra de grup de F1): sense
          // això es tanquen al `pointerdown` i el `click` no arriba mai al seu destí.
          if (target.closest('[data-sheet-outside-safe]')) {
            e.preventDefault();
            return;
          }
        }}
      >
        <SheetHeader className="text-left pb-4">
          {/* Close button + Channel indicator */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getChannelColor(channel)}`} />
              <span className="text-sm text-muted-foreground">{channel}</span>
              {getStatusBadge(status)}
            </div>
            <div className="flex items-center gap-1">
              {/*
                🆕 F2 — Entrada a Miranda des de la fitxa. Sense text predefinit: només obre
                el panell. El context (quina reserva es mira) ja viatja per l'URL, i és
                l'única manera d'obrir el xat quan la fitxa ocupa tota l'amplada (< sm).
              */}
              {!mirandaOpen && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openMiranda}
                  className="h-8 gap-1.5 text-xs text-muted-foreground"
                >
                  <Bot className="w-4 h-4" />
                  <span className="hidden sm:inline">Pregunta a Miranda</span>
                </Button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                aria-label="Tancar"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Beds24 ID */}
          {beds24Id && <div className="text-xs text-muted-foreground font-mono">#{beds24Id}</div>}

          {/* Guest name */}
          <SheetTitle className="text-xl">{guestName}</SheetTitle>

          {/* Company if exists */}
          {companyName && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="w-4 h-4" />
              {companyName}
            </div>
          )}
        </SheetHeader>

        <Separator className="my-4" />

        {/* Dates */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Dates
          </h3>
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="font-medium">{formatDate(checkIn)}</div>
              <div className="text-sm text-muted-foreground">Check-in</div>
            </div>
            <div className="text-muted-foreground">→</div>
            <div>
              <div className="font-medium">{formatDate(checkOut)}</div>
              <div className="text-sm text-muted-foreground">Check-out</div>
            </div>
          </div>
          {/* R4 — dates, habitació, preu i cancel·lar (només ADMIN; el botó porta el seu estat) */}
          <EditStayButton reservationId={reservation.id} onCancelled={onClose} />
          {/* F5 (runbook 7) — una habitació més al grup (només ADMIN i directa amb número de Beds24) */}
          <AddStayToGroupButton start={reservation} groupBeds24Ids={groupBeds24Ids} />
        </section>

        <Separator className="my-4" />

        {/* Guests - Expandible */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Viatgers
          </h3>
          <Collapsible open={showGuestsList} onOpenChange={setShowGuestsList}>
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1">
                <div className="font-medium">
                  {guestsRegistered} / {guestsExpected} registrats
                </div>
                <div className="text-sm text-muted-foreground">
                  {guestsExpected - guestsRegistered > 0
                    ? `Falten ${guestsExpected - guestsRegistered} per registrar`
                    : 'Tots registrats ✓'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {guestsRegistered < guestsExpected && (
                  <Badge variant="destructive" className="text-xs">
                    Pendent
                  </Badge>
                )}
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    {showGuestsList ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            <CollapsibleContent className="pt-2 space-y-2">
              {loadingGuests ? (
                <p className="text-sm text-muted-foreground text-center py-4 px-3 bg-muted/30 rounded-lg">
                  Carregant viatgers...
                </p>
              ) : guestsError ? (
                <p className="text-sm text-red-600 text-center py-4 px-3 bg-red-50 rounded-lg">
                  Error carregant viatgers: {(guestsError as any)?.message || 'Unknown error'}
                </p>
              ) : guests.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4 px-3 bg-muted/30 rounded-lg">
                  No hi ha viatgers registrats (ID reserva: {reservation?.id})
                </p>
              ) : (
                guests.map((viatger: any) => {
                  const fullName = `${viatger.nom} ${viatger.cognoms}`.trim();
                  const docNum = viatger.num_document;
                  const isTitular = viatger.es_titular || false;

                  return (
                    <div
                      key={viatger.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{fullName}</p>
                          {isTitular && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                              Titular
                            </span>
                          )}
                        </div>
                        {docNum && <p className="text-xs text-muted-foreground">{docNum}</p>}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 shrink-0"
                        onClick={() => {
                          setEditingGuest(viatger);
                          setShowEditGuestModal(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>
                  );
                })
              )}

              {/* Afegir viatger existent (Person o estada anterior) */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowAddGuestDialog(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Afegir viatger
              </Button>
            </CollapsibleContent>
          </Collapsible>

          {reservation?.id && (
            <AddViatgerDialog
              reservationId={reservation.id}
              open={showAddGuestDialog}
              onOpenChange={setShowAddGuestDialog}
            />
          )}
        </section>

        <Separator className="my-4" />

        {/* Status indicators */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Estat
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {/* Key status */}
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Key
                className={`w-5 h-5 ${
                  keyStatus === 'success'
                    ? 'text-green-500'
                    : keyStatus === 'warning'
                      ? 'text-yellow-500'
                      : keyStatus === 'error'
                        ? 'text-red-500'
                        : 'text-gray-400'
                }`}
              />
              <div className="text-sm">
                {keyStatus === 'success'
                  ? 'Codi enviat'
                  : keyStatus === 'error'
                    ? 'Sense codi'
                    : 'Pendent'}
              </div>
            </div>

            {/* Payment status */}
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <CreditCard
                className={`w-5 h-5 ${
                  paymentStatus === 'success'
                    ? 'text-green-500'
                    : paymentStatus === 'warning'
                      ? 'text-yellow-500'
                      : paymentStatus === 'pending'
                        ? 'text-gray-400'
                        : 'text-red-500'
                }`}
              />
              <div className="text-sm">
                {paymentStatus === 'success'
                  ? 'Pagat'
                  : paymentStatus === 'warning'
                    ? 'Parcial'
                    : 'Pendent'}
              </div>
            </div>
          </div>
        </section>

        <Separator className="my-4" />

        {/* Missatgeria WhatsApp — estat tasks per kind */}
        {reservation?.id && (
          <section className="space-y-3">
            <MessagingBadge
              reservationId={reservation.id}
              hasWhatsapp={(reservation as { hasWhatsapp?: boolean | null }).hasWhatsapp ?? null}
              beds24PropertyId={reservation.beds24PropertyId}
            />
          </section>
        )}

        <Separator className="my-4" />

        {/* Telèfon del hoste */}
        <section className="space-y-2">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Telèfon
          </h3>
          {editingPhone ? (
            <div className="flex gap-2">
              <input
                type="tel"
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                placeholder="+34 666 111 222"
                autoFocus
              />
              <Button
                size="sm"
                onClick={() =>
                  reservation &&
                  savePhoneMutation.mutate({ reservationId: reservation.id, phone: phoneValue })
                }
                disabled={savePhoneMutation.isPending}
              >
                {savePhoneMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Desar'
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingPhone(false);
                  setPhoneValue(reservation?.guestPhone || '');
                }}
              >
                ✕
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {reservation?.guestPhone ? (
                  <span className="text-sm font-medium">{reservation.guestPhone}</span>
                ) : (
                  <span className="text-sm text-muted-foreground italic">Sense telèfon</span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs ml-auto"
                  onClick={() => setEditingPhone(true)}
                >
                  Editar
                </Button>
              </div>
              {/* Tanda «dia a dia» §3.1: Welcome · Codi · Trucar, com al popover. Substitueix el
                  `tel:` del número i el botó «Trucar» d'Accions (en tenia dos). */}
              {reservation?.id && (
                <QuickWhatsAppActions
                  reservationId={reservation.id}
                  phone={reservation.guestPhone}
                  phoneE164={reservation.guestPhoneE164}
                  beds24PropertyId={reservation.beds24PropertyId}
                  showPhone={false}
                />
              )}
            </>
          )}
          <p className="text-xs text-muted-foreground">
            🌍{' '}
            {(() => {
              const countryCode = reservation?.guestCountry || reservation?.guestCountry2;
              if (!countryCode) return 'Sense país';
              // Convertir alpha-2 a alpha-3 si es necesario
              const iso3Code = countryCodeToNationality(countryCode) || countryCode;
              // Obtener nombre del país en el idioma actual
              return getCountryName(iso3Code, i18n.language) || countryCode;
            })()}
          </p>
        </section>

        <Separator className="my-4" />

        {/* Idioma de comunicació */}
        <section className="space-y-2">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            🌐 Idioma de comunicació
          </h3>
          <select
            value={currentLang}
            onChange={(e) => handleLangChange(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
          >
            <option value="">— Auto (per país del hoste)</option>
            <option value="ca">🇦🇩 Català</option>
            <option value="es">🇪🇸 Castellà</option>
            <option value="en">🇬🇧 Anglès</option>
            <option value="fr">🇫🇷 Francès</option>
            <option value="de">🇩🇪 Alemany</option>
            <option value="it">🇮🇹 Italià</option>
            <option value="pt">🇵🇹 Portuguès</option>
          </select>
          {!currentLang && (
            <p className="text-xs text-muted-foreground">
              Detectat:{' '}
              <strong>
                {reservation?.guestCountry2
                  ? `${reservation.guestCountry2} → ${detectLanguageFromCountry(reservation.guestCountry2)}`
                  : 'en (per defecte)'}
              </strong>
            </p>
          )}
        </section>

        <Separator className="my-4" />

        {/* Info Canal Beds24 (readonly) */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            📡 Info Canal (Beds24)
          </h3>
          <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1 font-mono">
            <div className="flex justify-between">
              <span className="text-muted-foreground">channel:</span>
              <span className="font-medium min-w-0 truncate">{channel || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">apiSource:</span>
              <span className="font-medium min-w-0 truncate">{reservation.apiSource || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">referer:</span>
              <span className="font-medium text-xs min-w-0 truncate">{reservation.referer || '-'}</span>
            </div>
          </div>
        </section>

        <Separator className="my-4" />

        {/* Client / Facturació */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Client / Facturació
          </h3>
          <div className="space-y-2">
            <ClientSearch
              value={selectedClient}
              onSelect={handleClientSelect}
              placeholder="Assignar client per facturació..."
            />
            {selectedClient && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm relative">
                <button
                  onClick={handleClearClient}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition-colors"
                  title="Treure client"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="font-medium text-green-800 pr-6">
                  {selectedClient.tradename ||
                    selectedClient.empresa ||
                    selectedClient.nom_complet ||
                    selectedClient.companyName ||
                    'Client seleccionat'}
                </div>
                {(selectedClient.taxId ||
                  selectedClient.nif_empresa ||
                  selectedClient.num_document) && (
                  <div className="text-green-600">
                    NIF/CIF:{' '}
                    {selectedClient.taxId ||
                      selectedClient.nif_empresa ||
                      selectedClient.num_document}
                  </div>
                )}
                {(selectedClient.contactEmail || selectedClient.email) && (
                  <div className="text-green-600">
                    {selectedClient.contactEmail || selectedClient.email}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <Separator className="my-4" />

        {/* 🆕 F3.5 — Casos d'ús escrits sobre aquesta reserva */}
        <ReservationUseCasesSection
          reservationId={reservation.id}
          beds24Id={reservation.beds24Id}
          groupBeds24Ids={groupBeds24Ids}
        />

        <Separator className="my-4" />

        {/* Actions */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Accions
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {/* Copiar link formulari de registre */}
            <Button
              variant="outline"
              className="justify-start gap-2 min-w-0"
              onClick={handleCopyFormUrl}
              disabled={!formUrl}
              title={formUrl ? formUrl : 'No hi ha formulari generat'}
            >
              {copiedFormUrl ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              <span className="truncate">{copiedFormUrl ? 'Copiat!' : 'Copiar formulari'}</span>
            </Button>
            {/* Enllaç de grup (F8): un sol formulari per a totes les reserves del grup */}
            {groupBeds24Ids.length > 1 && (
              <Button
                variant="outline"
                className="justify-start gap-2 min-w-0"
                onClick={() => generateGroupForm.mutate({ reservaId: reservation.id })}
                disabled={generateGroupForm.isPending}
                title={`Copia el missatge amb un sol formulari per a les ${groupBeds24Ids.length} reserves del grup`}
              >
                {generateGroupForm.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                <span className="truncate">Enllaç de grup</span>
              </Button>
            )}
            {/* Trucar i Enviar formulari: ara són a la fila Welcome · Registre · Codi · Trucar de la
                secció Telèfon (§7bis.4: el botó buit d'aquí hauria fet el mateix que Registre). */}
            {/* Email — pendent implementació */}
            <Button variant="outline" className="justify-start gap-2 min-w-0" disabled>
              <Mail className="w-4 h-4" />
              <span className="truncate">Email</span>
            </Button>
          </div>
        </section>

        <Separator className="my-4" />

        {/* Historial missatgeria de la reserva — eix: reservationId + telèfon */}
        <section className="space-y-3">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Historial missatgeria reserva
          </h3>
          <ReservationMessagesTab
            reservationId={reservation.id}
            guestPhone={reservation.guestPhone || undefined}
          />
        </section>

        {/* Debug Panel - Reservation Data */}
        <DebugPanel
          data={reservation}
          title="Debug: Reservation Data"
          type="reservation"
          className="mt-4"
        />

        {/* Modal Editar Viatger */}
        {editingGuest && (
          <EditViatgerModal
            open={showEditGuestModal}
            onOpenChange={setShowEditGuestModal}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['reservation-guests', reservation?.id] });
              toast.success('Viatger actualitzat');
            }}
            viatger={editingGuest}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

export default ReservationSheet;
